import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  ListSubheader,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { toEditFormDates, toEditedDatesPayload } from '../../../utils/dateOnly';
import Modal, { Field } from '../../common/Modal';
import { employeesApi, organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';

const EditPositionModal = ({
  isOpen,
  onClose,
  employeeId,
  positionEntry,
  onSuccess
}) => {
  const { showAlert } = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [positionsByOrgUnitType, setPositionsByOrgUnitType] = useState([]);
  const [positions, setPositions] = useState([]);
  const [positionSearchQuery, setPositionSearchQuery] = useState('');
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const [formData, setFormData] = useState({
    positionId: '',
    startDate: null,
    endDate: null
  });

  useEffect(() => {
    if (isOpen) {
      fetchPositions();
      if (positionEntry) {
        setFormData({
          positionId: positionEntry.position?.id || positionEntry.positionId || '',
          // ADR-197: pre-fill the stored calendar day, not its local-instant reading
          ...toEditFormDates(positionEntry)
        });
      }
    }
  }, [isOpen, positionEntry]);

  const fetchPositions = async () => {
    try {
      const positionsData = await organizationApi.getPositionsByOrgUnitType();
      setPositionsByOrgUnitType(positionsData);
      const allPositions = positionsData.reduce((acc, group) => [...acc, ...group.positions], []);
      setPositions(allPositions);
    } catch (error) {
      showAlert('Failed to load positions', 'error', error);
    }
  };

  const filterPositions = (positions, query) => {
    if (!query) return positions;
    return positions.filter(position =>
      position.label.toLowerCase().includes(query.toLowerCase())
    );
  };

  const getFilteredPositionsByOrgUnitType = () => {
    return positionsByOrgUnitType.map(group => ({
      ...group,
      positions: filterPositions(group.positions, positionSearchQuery)
    })).filter(group => group.positions.length > 0);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const payload = {};

      if (formData.positionId && formData.positionId !== positionEntry.position?.id) {
        payload.positionId = formData.positionId;
      }

      Object.assign(payload, toEditedDatesPayload(formData, positionEntry));

      await employeesApi.updatePositionHistory(employeeId, positionEntry.id, payload);
      showAlert('Position history updated successfully', 'success');
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error updating position history:', error);
      showAlert(error.message || 'Failed to update position history', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ positionId: '', startDate: null, endDate: null });
      setPositionSearchQuery('');
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Position"
      onSubmit={handleSubmit}
      submitText={isSubmitting ? 'Saving...' : 'Save Changes'}
      submitDisabled={!formData.positionId || !formData.startDate || isSubmitting}
      width="500px"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
        <Field>
          <FormControl fullWidth>
            <InputLabel>Position</InputLabel>
            <Select
              value={formData.positionId}
              onChange={(e) => setFormData(prev => ({ ...prev, positionId: e.target.value }))}
              label="Position"
              onClose={() => {
                setPositionSearchQuery('');
                setIsSelectOpen(false);
              }}
              onOpen={() => setIsSelectOpen(true)}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 450 }
                },
                autoFocus: false,
                disableAutoFocusItem: true,
                disableListWrap: true
              }}
            >
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 1,
                  p: 1
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search positions..."
                  value={positionSearchQuery}
                  onChange={(e) => setPositionSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoComplete="off"
                  sx={{
                    width: '100%',
                    backgroundColor: 'white',
                    '& .MuiInputBase-root': { borderRadius: 1 }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaSearch size={14} />
                      </InputAdornment>
                    ),
                  }}
                  autoFocus
                />
              </Box>
              {getFilteredPositionsByOrgUnitType().map(group => [
                <ListSubheader
                  key={group.orgUnitType.id}
                  sx={{
                    backgroundColor: '#f8f9fa',
                    lineHeight: '32px',
                    borderRadius: '4px',
                    color: '#6b7280',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    position: 'static'
                  }}
                >
                  {group.orgUnitType.name}
                </ListSubheader>,
                ...group.positions.map(position => (
                  <MenuItem
                    key={position.id}
                    value={position.id}
                    sx={{ pl: 4 }}
                  >
                    {position.label}
                  </MenuItem>
                ))
              ])}
              {getFilteredPositionsByOrgUnitType().length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                  No positions found
                </Box>
              )}
            </Select>
          </FormControl>
        </Field>

        <Field>
          <DatePicker
            label="Start Date"
            value={formData.startDate}
            onChange={(newValue) => {
              if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                setFormData(prev => ({ ...prev, startDate: newValue }));
              } else if (newValue === null) {
                setFormData(prev => ({ ...prev, startDate: null }));
              }
            }}
            format="DD/MM/YYYY"
            disabled={isSubmitting}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                inputProps: { placeholder: 'DD/MM/YYYY' }
              }
            }}
          />
        </Field>

        <Field>
          <DatePicker
            label="End Date (optional)"
            value={formData.endDate}
            onChange={(newValue) => {
              if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                setFormData(prev => ({ ...prev, endDate: newValue }));
              } else if (newValue === null) {
                setFormData(prev => ({ ...prev, endDate: null }));
              }
            }}
            format="DD/MM/YYYY"
            disabled={isSubmitting}
            slotProps={{
              textField: {
                fullWidth: true,
                inputProps: { placeholder: 'DD/MM/YYYY' }
              }
            }}
          />
          {formData.endDate ? (
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
              onClick={() => setFormData(prev => ({ ...prev, endDate: null }))}
            >
              <FaTimes size={12} /> Clear date
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: '#64748B', mt: 0.5 }}>
              Leave empty if this is the current position
            </Typography>
          )}
        </Field>
      </Box>
    </Modal>
  );
};

export default EditPositionModal;
